<script setup lang="ts">
import BadgeCharts from '~/components/ui/BadgeCharts.vue'
//* We keep using the old one since the new one doesn't handle the overflow hidden containers well
import VTooltip from '~/components/ui/VTooltip.vue'

import { useTrackChartsRank } from '~/composables/useTrackChartsRank'

import type { ChartsTrackStatus } from '~/types/ChartsTrackStatus'

type Props = {
  defaultTooltipPosition?: 'above' | 'below'
  hideChartsText?: boolean
  trackId?: number
}

const props = withDefaults(defineProps<Props>(), {
  defaultTooltipPosition: 'below',
  hideChartsText: false,
  trackId: undefined,
})

const { t } = useI18n()
const isTooltipDisplayed = ref(false)

const { rankStatus, isLoadingStatus, hasTrackChartsRankStatus } =
  useTrackChartsRank(props.trackId)

const status = computed<ChartsTrackStatus | null>(() => {
  const valuesMap = {
    active: 'active',
    pending: 'pending',
    'fallen-off': 'fallen-off',
    // added for type completion, we don't display this value
    refunded: null,
  } as const

  if (!hasTrackChartsRankStatus(rankStatus.value)) return 'pending'
  return valuesMap[rankStatus.value.status]
})

const statusText = computed(() => {
  if (!status.value) return

  return {
    active: t('charts.status.featuredTooltipText'),
    pending: t('charts.status.pendingTooltipText'),
    'fallen-off': t('charts.status.fallenOffTooltipText'),
  }[status.value]
})
</script>

<script lang="ts">
/**
 * Shows the Charts badge with a tooltip that explains the status of the track.
 * This component calls the api to compute the Charts upsell status.
 */
export default {}
</script>

<template>
  <VTooltip
    show-when-clicked
    html-tag="button"
    type="button"
    :position="defaultTooltipPosition"
    @show="isTooltipDisplayed = true"
    @hide="isTooltipDisplayed = false"
  >
    <template #default
      ><BadgeCharts
        v-if="status"
        :darken-bg="isTooltipDisplayed"
        :hide-text="hideChartsText"
        :status
    /></template>
    <template v-if="!isLoadingStatus" #tooltipText>{{ statusText }}</template>
    <template v-if="!isLoadingStatus" #cta>
      <NuxtLinkLocale
        v-if="status === 'active' || status === 'fallen-off'"
        to="/charts/"
        class="tw-mt-sm tw-underline hover:tw-underline md:tw-block"
        ><span class="tw-text-body-sm tw-font-medium">{{
          $t('charts.viewGrooverCharts')
        }}</span></NuxtLinkLocale
      >
      <NuxtLink
        v-else
        external
        rel="noopener"
        target="_blank"
        :to="$t('charts.faqLink')"
        class="tw-mt-2 tw-underline hover:tw-underline md:tw-block"
        ><span class="tw-text-body-sm tw-font-medium">{{
          $t('common.learnMore')
        }}</span></NuxtLink
      >
    </template>
  </VTooltip>
</template>
